<template>
  <v-container>
    <v-layout
      v-if="error.show">
      <PoweredByHeader />
      <v-flex xs-12>
        <Error
          :showError="error.show">
          {{error.message}}
        </Error>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';
import { parse } from 'qs';
import { Constants, Components } from '@spectorasoftware/homegauge-vue';
import Error from '@/components/Error.vue';

const { AccountTypes } = Constants;
const { PoweredByHeader } = Components;

export default {
  components: {
    Error,
    PoweredByHeader,
  },
  data: () => ({
    error: {
      show: false,
      message: '',
    },
  }),
  props: {
    redirectParams: {
      type: String,
      default: '',
    },
  },
  mounted() {
    this.$store.dispatch('unleash/fetch')
      .then(() => this.$store.dispatch('Person/getLoggedInPerson'))
      .then(res => {
        if (!this.isProductTypeMismatch(res?.type, this.hasRepItems())) {
          this.redirectToCheckout(res);
        }
      })
      .catch(() => { // possible 401/403
        this.redirectToSignUp();
      });
  },
  computed: {
    ...mapState('unleash', {
      enabledFeatures: state => state.enabledFeatures,
    }),
  },
  methods: {
    chargebeeCheckoutRedirectFlag() {
      return this.enabledFeatures['HG-1023-Chargebee-Checkout-Redirect'];
    },
    async redirectToCheckout(res) {
      const url = await this.generateRedirectUrl(res);

      window.location = ((res?.type === 'INSPECTOR' || res?.type === AccountTypes.REAL_ESTATE_PROFESSIONAL) && url)
        ? url
        : process.env.BASE_URL;
    },
    getEncodedSearchParams() {
      return window.location.search || '?';
    },
    getParams() {
      if (this.redirectParams) {
        return `?${this.$route.query.redirectParams}`;
      }

      return this.getEncodedSearchParams();
    },
    async generateRedirectUrl(res) {
      const encodedParams = this.getParams();
      return this.chargebeeCheckoutRedirectFlag()
        // eslint-disable-next-line no-return-await
        ? await this.generateRedirectUrlSubscriptionCheckout(encodedParams)
        : this.generateRedirectUrlNoProxy(res, encodedParams);
    },
    generateRedirectUrlNoProxy(res, encodedParams) {
      if (!res) {
        return null;
      }

      const CHARGE_BEE_URL = process.env.VUE_APP_CHARGEBEE_CHECKOUT_URL;
      const url = new URL(`${CHARGE_BEE_URL}${encodedParams}`);
      const phoneNumber = res.mobilePhone || res.workPhone || res.homePhone;
      const payload = {
        'customer[id]': res.personId,
        'customer[email]': res.primaryEmail,
        'customer[first_name]': res.givenName,
        'customer[last_name]': res.familyName,
        'customer[company]': res.companyName,
        'customer[phone]': phoneNumber,
        'billing_address[first_name]': res.givenName,
        'billing_address[last_name]': res.familyName,
        'billing_address[email]': res.primaryEmail,
        'billing_address[company]': res.companyName,
        'billing_address[country]': res.primaryAddress.country,
        'billing_address[phone]': phoneNumber,
        'billing_address[line1]': res.primaryAddress.address1,
        'billing_address[line2]': res.primaryAddress.address2,
        'billing_address[city]': res.primaryAddress.city,
        'billing_address[state_code]': res.primaryAddress.state,
        'billing_address[zip]': res.primaryAddress.postalCode,
        cf_shgi: res.legacyId,
      };

      Object.entries(payload).forEach(([k, v]) => {
        url.searchParams.append(k, v);
      });

      return url;
    },
    async generateRedirectUrlSubscriptionCheckout(encodedParams) {
      const decodedParams = parse(encodedParams, { ignoreQueryPrefix: true });
      const payload = {
        coupons: decodedParams.coupon_ids?.map(id => ({ id })),
        subscriptionItems: this.generateItemsList('subscription_items', decodedParams),
        chargeItems: this.generateItemsList('charge_items', decodedParams),
      };

      let url = null;

      try {
        const response = await this.$store.dispatch('Subscription/checkout', payload);
        url = new URL(response.body.hostedPage.url);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Subscription service checkout end-point. Error:', JSON.stringify(e, null, 2));
        if (e.success === false) {
          // eslint-disable-next-line no-alert
          alert(e.body.message);
        }
      }

      return url;
    },
    generateItemsList(key, decodedParams) {
      let items;

      if (key in decodedParams && 'item_price_id' in decodedParams[key]) {
        // eslint-disable-next-line camelcase
        let { item_price_id, quantity } = decodedParams[key];
        // eslint-disable-next-line camelcase
        item_price_id = Array.isArray(item_price_id) ? item_price_id : [];
        quantity = Array.isArray(quantity) ? quantity : [];
        items = item_price_id.map((id, i) => ({
          id,
          quantity: quantity[i] ? +quantity[i] : 1,
        }));
      }

      return items;
    },
    redirectToSignUp() {
      this.$router.push({
        path: 'signup',
        query: {
          reDirRouteName: 'chargebee',
          existingQueryParams: this.getEncodedSearchParams().slice(1),
          accountType: this.hasRepItems() ? AccountTypes.REAL_ESTATE_PROFESSIONAL : null,
        },
      });
    },
    hasRepItems() {
      const encodedParams = this.getParams();
      const decodedParams = parse(encodedParams, { ignoreQueryPrefix: true });
      const subscriptionItems = this.generateItemsList('subscription_items', decodedParams);
      const chargeItems = this.generateItemsList('charge_items', decodedParams);
      const RepRegex = /^rep-/gi;
      return subscriptionItems?.some(item => RepRegex.test(item.id))
      || chargeItems?.some(item => RepRegex.test(item.id));
    },
    isProductTypeMismatch(accountType, hasRepItems) {
      if (accountType !== AccountTypes.REAL_ESTATE_PROFESSIONAL && hasRepItems) {
        this.error.show = true;
        this.error.message = `This is a Real Estate Professional product, and you are logged in as a Inspector.
        Inspector products can be found at: ${process.env.VUE_APP_PRICING_URL}`;
        return true;
      }
      if (accountType === AccountTypes.REAL_ESTATE_PROFESSIONAL && !hasRepItems) {
        this.error.show = true;
        this.error.message = `This is a Inspector product, and you are logged in as a Real Estate Professional.
        Real Estate products can be found at: ${process.env.VUE_APP_REP_PRICING_URL}`;
        return true;
      }
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
.button-container {
  padding: 0px 10px;
}
</style>
